import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { Guard } from '@/modules/shared/components/guard';

const AdmHome = lazy(() => import('./pages/home'));
const Configuracoes = lazy(() => import('./pages/configuracoes'));

const CadastrosLayout = lazy(() => import('./pages/cadastros/_layout'));
const Disciplina = lazy(() => import('./pages/cadastros/disciplina'));
const ProfessorCad = lazy(() => import('./pages/cadastros/professor'));
const Turma = lazy(() => import('./pages/cadastros/turma'));
const Usuario = lazy(() => import('./pages/cadastros/usuario'));
const CadastrosIndex = lazy(() => import('./pages/cadastros/index'));
const CorrecaoLayout = lazy(() => import('./pages/correcao/_layout'));
const HistoricoProva = lazy(() => import('./pages/correcao/historico-prova'));
const HistoricoRedacao = lazy(() => import('./pages/correcao/historico-redacao'));

const QuizLayout = lazy(() => import('./pages/quiz/_layout'));
const SelecionarOpcao = lazy(() => import('./pages/quiz/selecionar-opcao'));
const GerarQuiz = lazy(() => import('./pages/quiz/gerar-quiz'));
const ListaQuestoes = lazy(() => import('./pages/quiz/lista-questoes'));
const HistoricoProfessor = lazy(() => import('./pages/quiz/historico-professor'));
const VisualizarResposta = lazy(() => import('./pages/quiz/visualizar-resposta'));

const RelatoriosLayout = lazy(() => import('./pages/relatorios/_layout'));
const RelatoriosIndex = lazy(() => import('./pages/relatorios/index'));
const RelatorioProvas = lazy(() => import('./pages/relatorios/relatorio-provas'));
const RelatorioRedacoes = lazy(() => import('./pages/relatorios/relatorio-redacoes'));
const RelatorioQuizzes = lazy(() => import('./pages/relatorios/relatorio-quizzes'));
const Visualizar = lazy(() => import('./pages/relatorios/visualizar'));

const AdmGuard = ({ children, allowedRoles }: {
  children: React.ReactNode;
  allowedRoles: any[];
}) => (
  <Suspense fallback={<div className="min-h-screen" />}>
    <Guard allowedRoles={allowedRoles}>
      {children}
    </Guard>
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: '/adm',
    element: <Navigate replace to="/" />,
    handle: { crumb: null }
  },
  {
    path: '/',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER']}>
        <AdmHome />
      </AdmGuard>
    ),
    handle: { crumb: 'Home' }
  },
  {
    path: '/configuracoes',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER']}>
        <Configuracoes />
      </AdmGuard>
    ),
    handle: { crumb: 'Configurações' }
  },
  {
    path: '/cadastros',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER']}>
        <CadastrosLayout />
      </AdmGuard>
    ),
    handle: { crumb: 'Cadastros' },
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<div className="min-h-screen" />}>
            <CadastrosIndex />
          </Suspense>
        ),
      },
      {
        path: 'disciplina',
        element: <Disciplina />,
        handle: { crumb: 'Disciplinas' }
      },
      {
        path: 'professor',
        element: <ProfessorCad />,
        handle: { crumb: 'Professores' }
      },
      {
        path: 'turma',
        element: <Turma />,
        handle: { crumb: 'Turmas' }
      },
      {
        path: 'usuario',
        element: <Usuario />,
        handle: { crumb: 'Usuários' }
      }
    ]
  },
  {
    path: '/correcao/historico-prova/:id',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'RESPONSAVEL']}>
        <HistoricoProva />
      </AdmGuard>
    ),
    handle: { crumb: 'Histórico de Provas' }
  },
  {
    path: '/correcao/historico-redacao/:id',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'RESPONSAVEL']}>
        <HistoricoRedacao />
      </AdmGuard>
    ),
    handle: { crumb: 'Histórico de Redações' }
  },
  {
    path: '/correcao/historico-redacao/lote/:loteUuid',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'RESPONSAVEL']}>
        <HistoricoRedacao />
      </AdmGuard>
    ),
    handle: { crumb: 'Alunos do Lote' }
  },
  {
    path: '/correcao/historico-redacao/lote/:loteUuid/aluno/:alunoIndex',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'RESPONSAVEL']}>
        <HistoricoRedacao />
      </AdmGuard>
    ),
    handle: { crumb: 'Resultado da Redação' }
  },
  {
    path: '/correcao/historico-prova/lote/:loteUuid',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'RESPONSAVEL']}>
        <HistoricoProva />
      </AdmGuard>
    ),
    handle: { crumb: 'Alunos do Lote' }
  },
  {
    path: '/correcao/historico-prova/lote/:loteUuid/aluno/:alunoIndex',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'RESPONSAVEL']}>
        <HistoricoProva />
      </AdmGuard>
    ),
    handle: { crumb: 'Resultado Prova' }
  },
  {
    path: '/correcao',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR']}>
        <CorrecaoLayout />
      </AdmGuard>
    ),
    handle: { crumb: 'Assistente de Correção' },
    children: [
      {
        index: true,
        element: <Navigate replace to="historico-prova" />,
      },
      {
        path: 'historico-prova',
        element: <HistoricoProva />,
        handle: { crumb: 'Histórico de Provas' }
      },
      {
        path: 'historico-redacao',
        element: <HistoricoRedacao />,
        handle: { crumb: 'Histórico de Redações' }
      }
    ]
  },
  {
    path: '/quiz/criar',
    element: <Navigate replace to="/quiz/historico-professor" />,
    handle: { crumb: null }
  },
  {
    path: '/quiz/professor',
    element: <Navigate replace to="/quiz" />,
    handle: { crumb: 'Quiz Professor' }
  },
  {
    path: '/quiz/aluno',
    element: <Navigate replace to="/quiz" />,
    handle: { crumb: 'Quiz Aluno' }
  },
  {
    path: '/quiz/professor/turma/:turmaId/materias',
    element: <Navigate replace to="/quiz/gerar-quiz" />,
    handle: { crumb: 'Selecionar Matéria IA' }
  },
  {
    path: '/quiz/responder/:quizId',
    element: <Navigate replace to="/quiz/lista-questoes" />,
    handle: { crumb: 'Responder Quiz' }
  },
  {
    path: '/quiz/responder/:quizId/resultado',
    element: <Navigate replace to="/quiz/historico-professor" />,
    handle: { crumb: 'Resultado Quiz' }
  },
  {
    path: '/quiz',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'ALUNO']}>
        <QuizLayout />
      </AdmGuard>
    ),
    handle: { crumb: 'Quiz' },
    children: [
      {
        index: true,
        element: <SelecionarOpcao />,
        handle: { crumb: 'Selecionar Opção' }
      },
      {
        path: 'gerar-quiz',
        element: <GerarQuiz />,
        handle: { crumb: 'Gerar Quiz' }
      },
      {
        path: 'lista-questoes',
        element: <ListaQuestoes />,
        handle: { crumb: 'Questões' }
      },
      {
        path: 'historico-professor',
        element: <HistoricoProfessor />,
        handle: { crumb: 'Histórico do Professor' }
      },
      {
        path: 'visualizar-resposta',
        element: <VisualizarResposta />,
        handle: { crumb: 'Visualizar Resposta do Aluno' }
      }
    ]
  },
  {
    path: '/relatorios',
    element: (
      <AdmGuard allowedRoles={['ADM', 'SUPER', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO']}>
        <RelatoriosLayout />
      </AdmGuard>
    ),
    handle: { crumb: 'Relatórios' },
    children: [
      {
        index: true,
        element: <RelatoriosIndex />,
      },
      {
        path: 'provas',
        element: <RelatorioProvas />,
        handle: { crumb: 'Provas e Simulados' }
      },
      {
        path: 'redacoes',
        element: <RelatorioRedacoes />,
        handle: { crumb: 'Redações' }
      },
      {
        path: 'quizes',
        element: <RelatorioQuizzes />,
        handle: { crumb: 'Quizzes Gerados' }
      },
      {
        path: 'visualizar-resposta',
        element: <VisualizarResposta />,
        handle: { crumb: 'Resposta do Quiz' }
      },
      {
        path: ':id',
        element: <Visualizar />,
        handle: { crumb: 'Visualizar Relatório' }
      },
      {
        path: 'aluno/:alunoId',
        element: <RelatoriosIndex />,
        handle: { crumb: 'Relatório do Aluno' }
      }
    ]
  }
];

export default routes;
